/**
 * The one door from the Operator pages to their data: `/api/v1/operator`.
 *
 * The Operator module is what was the Son of Basel portal (meetings,
 * recordings, Ask, insights, Talk, the clone and its settings), drawn by this
 * app since 2026-10-30 (docs/SON-OF-BASEL-MERGE.md). Its backend stays a
 * service of its own on another box. This app's API signs the person in and
 * forwards `/api/v1/operator/<rest>` to the service's `/api/<rest>` with a
 * signed identity (apps/api/src/lib/operator-gateway.ts), so a page calls
 * `operatorApi('/meetings')` where the old portal called `fetch('/api/meetings')`.
 *
 * Built like the Prospecting client (pages/prospecting/shared/api.ts):
 *
 *   - `GET` unless a body is given, which makes it `POST`. A plain object is
 *     sent as JSON, `FormData`, `URLSearchParams` and `Blob` as they are, so an
 *     upload reaches the service byte for byte (the gateway never parses it).
 *   - Never cached by the browser (`cache: 'no-store'`).
 *   - A refusal becomes an `OperatorError` whose message is the service's own
 *     sentence (`{ error: "..." }`) or the gateway's (`{ error, code }`).
 *   - Signed out (the gateway's 401, `code: UNAUTHENTICATED`): the cached
 *     session is dropped, so `RequireAuth` sends the person to sign in. Never
 *     on the three paths the public Talk demo calls (`OPERATOR_PUBLIC_PATHS`),
 *     where the caller is a guest with no session to drop.
 *   - No seat (403 `NO_SEAT`): a `no-access` error, which `OperatorAccess`
 *     turns into one sentence.
 */
import { buildQuery, type QueryParams } from '../../../lib/api'
import { qk, queryClient } from '../../../lib/queryClient'

/** Where the browser reaches the Operator service. Nothing else is ever called. */
export const OPERATOR_API_BASE = '/api/v1/operator'

/** `/api/v1/operator/stream/<id>`, for an href, an img or a media src the browser loads itself. */
export function operatorUrl(path: string, params?: QueryParams): string {
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${OPERATOR_API_BASE}${suffix}${buildQuery(params)}`
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * What kind of refusal it was, which is what a page decides on.
 *
 *   signed-out   this app's session is gone, so its sign-in screen takes over
 *   no-access    signed in, and this account has no Operator seat
 *   forbidden    signed in with a seat, and this one thing is not theirs to do
 *   not-found    the thing asked for is not there
 *   unreachable  the network, or the service did not answer (502, 503, 504)
 *   failed       anything else the service refused, with its own sentence
 */
export type OperatorErrorKind = 'signed-out' | 'no-access' | 'forbidden' | 'not-found' | 'unreachable' | 'failed'

export class OperatorError extends Error {
  readonly status: number
  readonly kind: OperatorErrorKind
  readonly code: string
  readonly details: Record<string, unknown>
  /**
   * The refusal's `details` when it was a sentence rather than an object, ''
   * otherwise. The bot's sandbox passes OpenRouter's own reason this way
   * (`{error: "OpenRouter API error: 402", details: "<the reason>"}`).
   */
  readonly detailText: string

  constructor(init: {
    status: number
    kind: OperatorErrorKind
    message: string
    code?: string
    details?: Record<string, unknown>
    detailText?: string
  }) {
    super(init.message)
    this.name = 'OperatorError'
    this.status = init.status
    this.kind = init.kind
    this.code = init.code ?? ''
    this.details = init.details ?? {}
    this.detailText = init.detailText ?? ''
  }

  /** True when trying again in a moment could work. */
  get isTransient(): boolean {
    return this.kind === 'unreachable' || this.status === 429
  }
}

export function isOperatorError(value: unknown): value is OperatorError {
  return value instanceof OperatorError
}

/** The sentence the person reads when they have no seat. */
export const NO_ACCESS_MESSAGE = 'This account cannot open Operator. Ask Basel to add you.'

/** What the browser says when it could not reach the server at all. */
export const OFFLINE_MESSAGE = 'Operator did not answer. Check your connection and try again.'

/** What a refusal carried, read off its body. */
interface Refusal {
  message: string | null
  code: string
  details: Record<string, unknown>
  detailText: string
}

/** The `{error}`, `{error, code}` or `{error: {code, message}}` a refusal carried. */
function refusalOf(payload: unknown): Refusal {
  if (typeof payload !== 'object' || payload === null) return { message: null, code: '', details: {}, detailText: '' }
  const body = payload as { error?: unknown; code?: unknown; details?: unknown; message?: unknown }
  const objectDetails = (value: unknown): Record<string, unknown> => (typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {})
  const textDetails = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')
  const outer = { details: objectDetails(body.details), detailText: textDetails(body.details) }
  if (typeof body.error === 'string' && body.error.trim() !== '') {
    // `{error: "forbidden", message: "Admin access required."}`: the error is
    // a code and the sentence is in `message` (the service's Talk routes).
    if (/^[a-z_-]+$/i.test(body.error.trim()) && typeof body.message === 'string' && body.message.trim() !== '') {
      return { message: body.message, code: typeof body.code === 'string' ? body.code : body.error.trim(), ...outer }
    }
    return { message: body.error, code: typeof body.code === 'string' ? body.code : '', ...outer }
  }
  if (typeof body.error === 'object' && body.error !== null) {
    const inner = body.error as { code?: unknown; message?: unknown; details?: unknown }
    return {
      message: typeof inner.message === 'string' && inner.message.trim() !== '' ? inner.message : null,
      code: typeof inner.code === 'string' ? inner.code : '',
      details: objectDetails(inner.details),
      detailText: textDetails(inner.details),
    }
  }
  if (typeof body.message === 'string' && body.message.trim() !== '') {
    return { message: body.message, code: typeof body.code === 'string' ? body.code : '', ...outer }
  }
  return { message: null, code: typeof body.code === 'string' ? body.code : '', ...outer }
}

/**
 * A refusal, sorted into the kind a page decides on. Pure, so it is covered
 * without a network. Only the gateway's own 401 (`UNAUTHENTICATED`) means this
 * app's session is gone. Any other 401 came from the service, which only says
 * that when it refused this app's sign-in, and reads as a seat problem.
 */
export function operatorErrorFrom(status: number, payload: unknown): OperatorError {
  const refusal = refusalOf(payload)
  const message = refusal.message === null ? `Request failed (${status})` : refusal.message
  const base = { status, code: refusal.code, details: refusal.details, detailText: refusal.detailText }
  if (status === 401) {
    return refusal.code === 'UNAUTHENTICATED'
      ? new OperatorError({ ...base, kind: 'signed-out', message: 'Your session has ended. Sign in again.' })
      : new OperatorError({ ...base, kind: 'no-access', message: NO_ACCESS_MESSAGE })
  }
  if (status === 403) {
    return refusal.code === 'NO_SEAT'
      ? new OperatorError({ ...base, kind: 'no-access', message: NO_ACCESS_MESSAGE })
      : new OperatorError({ ...base, kind: 'forbidden', message })
  }
  if (status === 404) return new OperatorError({ ...base, kind: 'not-found', message })
  if (status === 0 || status === 502 || status === 503 || status === 504) {
    return new OperatorError({ ...base, kind: 'unreachable', message: refusal.message === null ? OFFLINE_MESSAGE : message })
  }
  return new OperatorError({ ...base, kind: 'failed', message })
}

/**
 * The paths a visitor with no session may call (the public Talk demo,
 * `/operator/demo`): the gateway forwards them without an identity. A 401 on
 * one of them is the demo refusing a guest, never this app's session ending,
 * so it must not drop the cache or send anybody to sign in.
 */
export const OPERATOR_PUBLIC_PATHS: readonly string[] = ['/talk/access', '/talk/token', '/talk/spawn', '/sob/signup-requirements']

export function isOperatorPublicPath(path: string): boolean {
  const bare = (path.startsWith('/') ? path : `/${path}`).split(/[?#]/)[0]!.replace(/\/+$/, '')
  return OPERATOR_PUBLIC_PATHS.includes(bare)
}

/** This app's session is gone: drop the cache and ask for the session again, as `useSessionExpiryWatcher` does. */
function signedOut(): void {
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' })
  void queryClient.invalidateQueries({ queryKey: qk.session() })
}

/**
 * Any read found the seat gone (403 `NO_SEAT`): ask GET /me again, so the
 * gate and the sidebar learn it now rather than when /me is next stale.
 * `accessFrom` prefers that refusal to the cached answer.
 */
function seatLost(path: string): void {
  const bare = (path.startsWith('/') ? path : `/${path}`).split(/[?#]/)[0]!.replace(/\/+$/, '')
  if (bare === '/me' || isOperatorPublicPath(path)) return
  void queryClient.invalidateQueries({ queryKey: [OPERATOR_QUERY_ROOT, 'me'], exact: true })
}

/** What a failed request does to the rest of the app, by its kind. */
function afterRefusal(error: OperatorError, path: string): void {
  if (error.kind === 'signed-out' && !isOperatorPublicPath(path)) signedOut()
  if (error.kind === 'no-access') seatLost(path)
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export type OperatorBody = Record<string, unknown> | unknown[] | FormData | URLSearchParams | Blob

export interface OperatorRequestOptions {
  /** GET by default, POST when a body is given. */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** A plain object or array is sent as JSON. FormData, URLSearchParams and Blob are sent as they are. */
  body?: OperatorBody
  /** Added to the path as a query string. Arrays repeat the key. */
  params?: QueryParams
  signal?: AbortSignal
  headers?: Record<string, string>
}

function isRawBody(body: OperatorBody): body is FormData | URLSearchParams | Blob {
  return (
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) ||
    (typeof Blob !== 'undefined' && body instanceof Blob)
  )
}

/** The fetch options for one request. Exported for the test. */
export function requestInit(options: OperatorRequestOptions): RequestInit {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  let body: BodyInit | undefined
  if (options.body !== undefined) {
    if (isRawBody(options.body)) {
      body = options.body
    } else {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(options.body)
    }
  }
  return {
    method: options.method ?? (options.body !== undefined ? 'POST' : 'GET'),
    credentials: 'same-origin',
    cache: 'no-store',
    headers,
    body,
    signal: options.signal,
  }
}

async function readAnswer(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '')
  if (text === '') return {}
  try {
    return JSON.parse(text) as unknown
  } catch {
    return { error: response.ok ? undefined : text.slice(0, 300) }
  }
}

/**
 * One request, thrown on refusal.
 *
 * ```ts
 * const meetings = await operatorApi<Meeting[]>('/meetings')
 * await operatorApi(`/meetings/${id}/resummarize`, { method: 'POST' })
 * ```
 */
export async function operatorApi<T = unknown>(path: string, options: OperatorRequestOptions = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(operatorUrl(path, options.params), requestInit(options))
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new OperatorError({ status: 0, kind: 'unreachable', message: OFFLINE_MESSAGE })
  }
  const data = await readAnswer(response)
  if (!response.ok) {
    const error = operatorErrorFrom(response.status, data)
    afterRefusal(error, path)
    throw error
  }
  return data as T
}

/**
 * One request that answers a raw `Response` (a file, a stream, a blob), for
 * the few calls that are not JSON. Refusals are thrown as `operatorApi` throws them.
 */
export async function operatorFetch(path: string, options: OperatorRequestOptions = {}): Promise<Response> {
  let response: Response
  try {
    // Not JSON back, so no `Accept: application/json`, but a JSON body still says so.
    const init = requestInit(options)
    const { Accept: _accept, ...sent } = init.headers as Record<string, string>
    response = await fetch(operatorUrl(path, options.params), { ...init, headers: sent })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new OperatorError({ status: 0, kind: 'unreachable', message: OFFLINE_MESSAGE })
  }
  if (!response.ok) {
    const error = operatorErrorFrom(response.status, await readAnswer(response))
    afterRefusal(error, path)
    throw error
  }
  return response
}

/** The verbs, for the pages that read better with them. */
export const operator = {
  get: <T = unknown>(path: string, options: Omit<OperatorRequestOptions, 'method' | 'body'> = {}) => operatorApi<T>(path, { ...options, method: 'GET' }),
  post: <T = unknown>(path: string, body?: OperatorBody, options: Omit<OperatorRequestOptions, 'method' | 'body'> = {}) =>
    operatorApi<T>(path, { ...options, method: 'POST', ...(body === undefined ? {} : { body }) }),
  put: <T = unknown>(path: string, body?: OperatorBody, options: Omit<OperatorRequestOptions, 'method' | 'body'> = {}) =>
    operatorApi<T>(path, { ...options, method: 'PUT', ...(body === undefined ? {} : { body }) }),
  patch: <T = unknown>(path: string, body?: OperatorBody, options: Omit<OperatorRequestOptions, 'method' | 'body'> = {}) =>
    operatorApi<T>(path, { ...options, method: 'PATCH', ...(body === undefined ? {} : { body }) }),
  delete: <T = unknown>(path: string, body?: OperatorBody, options: Omit<OperatorRequestOptions, 'method' | 'body'> = {}) =>
    operatorApi<T>(path, { ...options, method: 'DELETE', ...(body === undefined ? {} : { body }) }),
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

/** The root every Operator query sits under, so one call can refresh them all. */
export const OPERATOR_QUERY_ROOT = 'operator'

/** The cache key for a path and its query: `['operator', 'meetings', {q: 'x'}]`. */
export function operatorKey(path: string, params?: Record<string, unknown>): readonly unknown[] {
  const segments = path
    .split('?')[0]!
    .split('/')
    .filter((segment) => segment !== '')
  return params === undefined ? [OPERATOR_QUERY_ROOT, ...segments] : [OPERATOR_QUERY_ROOT, ...segments, params]
}

/** The keys more than one page reads. */
export const operatorQueryKeys = {
  all: () => [OPERATOR_QUERY_ROOT] as const,
  /** GET /me: who is signed in there, their role, power admin, and their clone. */
  me: () => [OPERATOR_QUERY_ROOT, 'me'] as const,
  meetings: () => [OPERATOR_QUERY_ROOT, 'meetings'] as const,
  meeting: (id: string) => [OPERATOR_QUERY_ROOT, 'meetings', id] as const,
  botState: () => [OPERATOR_QUERY_ROOT, 'bot', 'state'] as const,
  clone: () => [OPERATOR_QUERY_ROOT, 'clones'] as const,
  /** GET /talk/access: whether the power admin's timed Talk demo is open. Public. */
  talkAccess: () => [OPERATOR_QUERY_ROOT, 'talk', 'access'] as const,
}

/**
 * Whether a refusal means a poll should stop: the seat is gone (`no-access`),
 * the session ended (`signed-out`), or this thing is not the person's
 * (`forbidden`). Asking again every few seconds would only be refused again.
 */
export function stopsPolling(error: unknown): boolean {
  return isOperatorError(error) && (error.kind === 'no-access' || error.kind === 'forbidden' || error.kind === 'signed-out')
}

/**
 * A `refetchInterval` for an Operator poll: every `ms` (or never, for
 * `false`), until the last answer was a refusal `stopsPolling` names. A later
 * read that succeeds (a refocus, a retry) clears the error and the poll goes on.
 *
 * ```ts
 * useQuery({ queryKey, queryFn, refetchInterval: operatorPoll(2000) })
 * ```
 */
export function operatorPoll(ms: number | false): (query: { state: { error: unknown } }) => number | false {
  return (query) => (ms === false || stopsPolling(query.state.error) ? false : ms)
}

/** Retry a read only when the service was out of reach, twice at most. */
export function operatorRetry(failureCount: number, error: unknown): boolean {
  return failureCount < 2 && isOperatorError(error) && error.isTransient
}
